/* eslint-disable no-restricted-globals */
import _ from 'lodash';

export const isNullOrUndefined = (element) => {
    return element === null || element === undefined;
};

export const hasValue = (object) => {
    if (typeof object === 'number') {
        return isNumber(object);
    }

    if (typeof object === 'function') {
        return !isNullOrUndefined(object);
    }

    return !isNullOrUndefined(object) && !_.isEmpty(object);
};

export const isNumber = (value) => {
    return !isNullOrUndefined(value) && !isNaN(value) && value !== '';
};

export const isBoolean = (value) => {
    return typeof value === 'boolean';
};

export const isScriptEmbedded = (scriptUrl = '', innerHtml = '') => {
    if (hasValue(scriptUrl) || hasValue(innerHtml)) {
        const scriptHtmlElements = document.getElementsByTagName('script');
        const scripts = [].slice.call(scriptHtmlElements);
        return scripts.some((script) => scriptHasSrc(script, scriptUrl) || scriptHasInnerHtml(script, innerHtml));
    }

    return false;
};

export const scriptHasSrc = (script = null, scriptUrl = '') => {
    if (!isNullOrUndefined(script) && hasValue(script.src) && hasValue(scriptUrl)) {
        return script.src.toLowerCase() === scriptUrl.toLowerCase();
    }

    return false;
};

export const scriptHasInnerHtml = (script = null, innerHtml = '') => {
    if (!isNullOrUndefined(script) && hasValue(script.innerHTML) && hasValue(innerHtml)) {
        return script.innerHTML === innerHtml;
    }

    return false;
};

export const embedScript = (sourceUrl = '', loadScriptAsync = true, callBack = null, useNoModule = false, scriptType = '', innerHtml = '') => {
    if (hasValue(sourceUrl) || hasValue(innerHtml)) {
        const script = document.createElement('script');
        script.async = !isNullOrUndefined(loadScriptAsync) ? loadScriptAsync : true;
        script.type = 'text/javascript';

        if (hasValue(callBack)) {
            script.onload = callBack;
        }

        if (isBoolean(useNoModule) && useNoModule) {
            script.noModule = useNoModule;
        }

        if (hasValue(scriptType)) {
            script.type = scriptType;
        }

        if (hasValue(innerHtml)) {
            script.innerHTML = innerHtml;
        } else {
            script.src = sourceUrl;
        }

        document.head.appendChild(script);
    }
};

export const parametersDidUpdate = (oldParams, newParams) => {
    return hasValue(newParams) && !_.isEqual(oldParams, newParams);
};

export const toggleCssClass = (element, className, criteria) => {
    if (isNullOrUndefined(element) || !hasValue(className)) {
        return;
    }

    if (criteria) {
        if (!element.classList.contains(className)) {
            element.classList.add(className);
        }
    } else if (element.classList.contains(className)) {
        element.classList.remove(className);
    }
};

export const getElementsByQuery = (query = '', containers = {}) => {
    const elements = [];

    if (!hasValue(containers) || !hasValue(query)) {
        return elements;
    }
    Object.keys(containers).forEach((key) => {
        if (containers[key][0] === null) {
            return;
        }
        containers[key][0].querySelectorAll(query).forEach((el) => {
            elements.push(el);
        });
    });

    return elements;
};

export const getElementByQuery = (query = '', containers = {}, index = 0) => {
    const elements = getElementsByQuery(query, containers);

    if (!hasValue(elements)) {
        return undefined;
    }

    return elements[index];
};

export const getSiblingsByQuery = (query = '', element = null) => {
    if (isNullOrUndefined(element) || !hasValue(query)) {
        return null;
    }

    return getElementsByQuery(query, { container: [element.parentElement] });
};


export const getSiblingByQuery = (query = '', element = null, index = 0) => {
    const siblings = getSiblingsByQuery(query, element);

    if (!hasValue(siblings)) {
        return undefined;
    }

    return siblings[index];
};

export const setHtmlIdAttribute = (element = null, id = '') => {
    if (hasValue(id) && hasValue(element) && !_.isEqual(element.id, id)) {
        element.id = id;
    }
};

export const isRefMounted = (ref = null) => {
    return hasValue(ref) && hasValue(ref.current);
};

export const getProperty = (obj = {}, propertyToReturn = '') => {
    if (hasValue(obj) && hasValue(propertyToReturn)) return obj[propertyToReturn];

    return undefined;
};

export const findElementByProperty = (array = [], property = '', value = null) => {
    if (!hasValue(array) || !hasValue(property) || !hasValue(value)) {
        return undefined;
    }

    return array.find((el) => hasValue(el[property]) && el[property] === value);
};

export const findElement = (array = [], value = null) => {
    if (!hasValue(array) || !hasValue(value)) {
        return undefined;
    }

    return array.find((el) => el === value);
};

export const getObjectKeyByValue = (object = {}, value = null) => {
    let foundKey = '';

    if (!hasValue(object) || !hasValue(value)) {
        return foundKey;
    }

    Object.keys(object).every((key) => {
        if (object[key] === value) {
            foundKey = key;
            return false;
        }

        return true;
    });

    return foundKey;
};

export const uuidv4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        // eslint-disable-next-line no-bitwise, no-mixed-operators
        const r = Math.random() * 16 | 0;
        // eslint-disable-next-line no-bitwise, no-mixed-operators
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const safeParseJSON = (expression) => {
    if (isNullOrUndefined(expression)) {
        return undefined;
    }

    try {
        const parsedJson = JSON.parse(expression);

        return parsedJson;
    } catch (e) {
        return undefined;
    }
};


export const safeParseToInt = (value) => {
    try {
        return parseInt(value);
    } catch (error) {
        return undefined;
    }
}

export const stringToBoolean = (str) => {
    if (!hasValue(str)) {
        return false;
    }

    return String(str).toLowerCase() === 'true';
};

export const stringToArray = (str, separator = undefined) => {
    if (separator === undefined) {
        // Default separators: comma, semicolon, or space
        return str.split(/[,\s;]+/);
    }

    return str.split(separator);
};
